export class ReportInfo {
    public id: string
    public title: string

    constructor(data: any) {
        this.id = data.id || ''
        this.title = data.title || ''
    }
}

export class ReportGroup {
    public title: string
    public reports: ReportInfo[]

    constructor(data: any) {
        this.title = data.title || ''
        this.reports = data.reports?.map((r: any) => new ReportInfo(r)) || []
    }
}

export enum ReportFilterType {
    TimeRange = 'time-range',
    SmallTimeRange = 'small-time-range',
    SingleSelect = 'single-select',
    MultiSelect = 'multi-select',
    AutoComplete = 'auto-complete',
    CheckBox = 'check-box'
}

export class ReportFilter {
    public name: string
    public title: string
    public type: ReportFilterType
    public datasets: string[]
    public values: any[][]

    constructor(data: any) {
        this.name = data.name || ''
        this.title = data.title || ''
        this.type = data.type || ReportFilterType.TimeRange
        this.datasets = data.datasets || []
        this.values = (data.values || []).map((v : any) => typeof v === 'string' ? [v] : v)
    }
}

export class ReportTableColumn {
    public title: string
    public value: string
    public link: string
    public format: string

    constructor(data: any) {
        this.title = data.title || ''
        this.value = data.value || ''
        this.link = data.link || ''
        this.format = data.format || ''
    }
}

export class ReportTable {
    public title: string
    public dataset: string
    public columns: ReportTableColumn[]
    public style: string

    constructor(data: any) {
        this.title = data.title || ''
        this.dataset = data.dataset || ''
        this.columns = data.columns?.map((c: any) => new ReportTableColumn(c))
        this.style = data.style || ''
    }
}

export class ReportChartAxis {
    public title: string
    public value: string
    public format: string

    constructor(data: any) {
        this.title = data.title || ''
        this.value = data.value || ''
        this.format = data.format || ''
    }
}

export enum ReportChartType {
    Line = 'line',
    Bar = 'bar',
    Pie = 'pie',
    Bubble = 'bubble'
}

export enum ReportChartOption {
    FixedColor = 'fixed-color',
    Dots = 'dots',
    Stacked = 'stacked',
    Horizontal = 'horizontal',
    DailyTimeline = 'daily-timeline',
    HourlyTimeline = 'hourly-timeline',
    Labels = 'labels',
    Legend = 'legend'
}

export class ReportChart {
    public title: string
    public type: ReportChartType
    public options: ReportChartOption[]
    public minimum: number
    public dataset: string
    public axis: ReportChartAxis
    public series: ReportChartAxis[]
    public style: string

    constructor(data: any) {
        this.title = data.title || ''
        this.type = data.type || ReportChartType.Line
        this.options = data.options || []
        this.minimum = data.minimum || 0
        this.dataset = data.dataset || ''
        this.axis = new ReportChartAxis(data.axis || {})
        this.series = data.series?.map((s: any) => new ReportChartAxis(s)) || []
        this.style = data.style || ''
    }
}

export class ReportTemplate {
    public filters: ReportFilter[]
    public tables: ReportTable[]
    public charts: ReportChart[]

    constructor(data: any) {
        this.filters = data.filters?.map((f: any) => new ReportFilter(f)) || []
        this.tables = data.tables?.map((t: any) => new ReportTable(t)) || []
        this.charts = data.charts?.map((c: any) => new ReportChart(c)) || []
    }
}

export class ReportData {
    public name: string
    public columns: string[]
    public values: any[][]

    constructor(data: any) {
        this.name = data.name || ''
        this.columns = data.columns || []
        this.values = data.data || []
    }
}

// type: "result" | "clarification" | "error"
export class AiReportColumn {
    public name: string
    public label: string
    public type: string

    constructor(data: any) {
        this.name = data.name || ''
        this.label = data.label || ''
        this.type = data.type || 'string'
    }
}

export class AiReportResponse {
    public type: string
    public sql: string | undefined
    public columns: AiReportColumn[]
    public rows: any[][]
    public explanation: string | undefined
    public tokensUsed: number | undefined
    public executionTimeMs: number | undefined
    public message: string | undefined

    constructor(data: any) {
        this.type = data.type || 'error'
        this.sql = data.sql
        this.columns = data.columns?.map((c: any) => new AiReportColumn(c)) || []
        this.rows = data.rows || []
        this.explanation = data.explanation
        this.tokensUsed = data.tokensUsed
        this.executionTimeMs = data.executionTimeMs
        this.message = data.message
    }
}

export interface AiReportChatMessage {
    role: string
    content: string
}

export class Plan {
    public planId : string
    public planName : string
    public description : string
    public duration : string
    public orderNumber : string
    public price : string
    public maxDevices: string
    public application : string
    public active : boolean
    public status : boolean
    public trial : boolean
    public updated : string

    constructor(planId: string, planName: string, description: string, duration: string, orderNumber: string, price: string,
                maxDevices: string,  application : string, active: boolean, status: boolean, trial: boolean, updated: string) {
        this.planId = planId
        this.planName = planName
        this.description = description
        this.duration = duration
        this.orderNumber = orderNumber
        this.price = price
        this.maxDevices = maxDevices
        this.application = application
        this.active = active
        this.status = status
        this.trial = trial
        this.updated = updated
    }
}

export class StripeConfig {
    public stripeConfigurationId: string
    public stripeName: string
    public privateKey: string
    public apiKey: string
    public endpointSecret: string
    public checkoutDomain: string
    public customerPortalLink: string
    public application : string
    public percentage: number
    public active: boolean
    public status: boolean

    constructor(stripeConfigurationId: string, stripeName: string, privateKey: string, apiKey: string, endpointSecret: string,
                checkoutDomain: string, customerPortalLink: string,  application : string, percentage: number, active: boolean, status: boolean) {
        this.stripeConfigurationId = stripeConfigurationId;
        this.stripeName = stripeName;
        this.privateKey = privateKey;
        this.apiKey = apiKey;
        this.endpointSecret = endpointSecret;
        this.checkoutDomain = checkoutDomain;
        this.customerPortalLink = customerPortalLink;
        this.application = application;
        this.percentage = percentage;
        this.active = active;
        this.status = status;
    }
}

export class PaypalConfig {
    public paypalConfigurationId: string;
    public paypalName: string;
    public brandName: string;
    public clientId: string;
    public clientSecret: string;
    public webhookId: string;
    public apiBase: string;
    public redirectUrl: string;
    public application: string;
    public percentage: number;
    public active: boolean;
    public status: boolean;

    constructor(paypalConfigurationId: string, paypalName: string, brandName: string, clientId: string, clientSecret: string,
                webhookId: string, apiBase: string, redirectUrl: string, application: string, percentage: number, active: boolean, status: boolean) {
        this.paypalConfigurationId = paypalConfigurationId;
        this.paypalName = paypalName;
        this.brandName = brandName;
        this.clientId = clientId;
        this.clientSecret = clientSecret;
        this.webhookId = webhookId;
        this.apiBase = apiBase;
        this.redirectUrl = redirectUrl;
        this.application = application;
        this.percentage = percentage;
        this.active = active;
        this.status = status;
    }
}

export class SquareConfig {
    public squareConfigurationId: string;
    public squareName: string;
    public accessToken: string;
    public locationId: string;
    public webhookSignatureKey: string;
    public webhookUrl: string;
    public redirectUrl: string;
    public apiBase: string;
    public application: string;
    public percentage: number;
    public active: boolean;
    public status: boolean;

    constructor(squareConfigurationId: string, squareName: string, accessToken: string, locationId: string,
                webhookSignatureKey: string, webhookUrl: string, redirectUrl: string, apiBase: string,
                application: string, percentage: number, active: boolean, status: boolean) {
        this.squareConfigurationId = squareConfigurationId;
        this.squareName = squareName;
        this.accessToken = accessToken;
        this.locationId = locationId;
        this.webhookSignatureKey = webhookSignatureKey;
        this.webhookUrl = webhookUrl;
        this.redirectUrl = redirectUrl;
        this.apiBase = apiBase;
        this.application = application;
        this.percentage = percentage;
        this.active = active;
        this.status = status;
    }
}

export class CpayConfig {
    public cpayConfigurationId: string;
    public cpayName: string;
    public secret: string;
    public checkoutPage: string;
    public paymentGateway: string;
    public application: string;
    public percentage: number;
    public whatsappEnabled: boolean;
    public active: boolean;
    public status: boolean;

    constructor(cpayConfigurationId: string, cpayName: string, secret: string, checkoutPage: string,
                paymentGateway: string, application: string, percentage: number, whatsappEnabled: boolean,
                active: boolean, status: boolean) {
        this.cpayConfigurationId = cpayConfigurationId;
        this.cpayName = cpayName;
        this.secret = secret;
        this.checkoutPage = checkoutPage;
        this.paymentGateway = paymentGateway;
        this.application = application;
        this.percentage = percentage;
        this.whatsappEnabled = whatsappEnabled;
        this.active = active;
        this.status = status;
    }
}

export class PlisioConfig {
    public plisioConfigurationId: string;
    public plisioName: string;
    public apiKey: string;
    public redirectUrl: string;
    public callbackUrl: string;
    public failInvoiceUrl: string;
    public application: string;
    public active: boolean;
    public status: boolean;

    constructor(plisioConfigurationId: string, plisioName: string, apiKey: string, redirectUrl: string,
                callbackUrl: string, failInvoiceUrl: string, application: string, active: boolean, status: boolean) {
        this.plisioConfigurationId = plisioConfigurationId;
        this.plisioName = plisioName;
        this.apiKey = apiKey;
        this.redirectUrl = redirectUrl;
        this.callbackUrl = callbackUrl;
        this.failInvoiceUrl = failInvoiceUrl;
        this.application = application;
        this.active = active;
        this.status = status;
    }
}

// One row = one credit-card-eligible account (Stripe/Square/cPay/PayPal), all sharing a single
// 100% pool. Plisio is not part of this pool and has no percentage/CardPaymentConfiguration row.
export class CardPaymentConfiguration {
    public provider: string;
    public configurationId: string;
    public name: string;
    public percentage: number;
    public active: boolean;
    public status: boolean;
    // Not returned by the BE yet (see payment-management-admin-api-contract.md open questions).
    // Read defensively everywhere it's used so the page degrades gracefully until it ships.
    public application: string;

    constructor(provider: string, configurationId: string, name: string, percentage: number,
                active: boolean, status: boolean, application: string) {
        this.provider = provider;
        this.configurationId = configurationId;
        this.name = name;
        this.percentage = percentage;
        this.active = active;
        this.status = status;
        this.application = application;
    }
}

export class CardPaymentConfigurations {
    public cardPaymentConfigurationList: CardPaymentConfiguration[];

    constructor(cardPaymentConfigurationList: CardPaymentConfiguration[]) {
        this.cardPaymentConfigurationList = cardPaymentConfigurationList;
    }
}


export class StripePlan {
    public stripePlanId : string
    public stripeConfigurationId : string
    public planId : string
    public stripeProductId : string
    public stripePriceId : string


    constructor(stripePlanId: string, stripeConfigurationId: string, planId: string, stripeProductId: string, stripePriceId: string) {
        this.stripePlanId = stripePlanId;
        this.stripeConfigurationId = stripeConfigurationId;
        this.planId = planId;
        this.stripeProductId =  stripeProductId;
        this.stripePriceId = stripePriceId;
    }
}

export class Coupon {
    public couponId : number
    public couponCode : string
    public percentage : number
    public active : boolean
    public status : boolean
    public updated : string

    constructor(couponId: number, couponCode: string, percentage: number, active: boolean, status: boolean, updated: string) {
        this.couponId = couponId
        this.couponCode = couponCode
        this.percentage = percentage
        this.active = active
        this.status = status
        this.updated = updated
    }
}

export class StripeCoupon {
    public stripeCouponId : number
    public stripeConfigurationId : number
    public stripeCouponCode : string
    public couponId : number


    constructor(stripeCouponId: number, stripeConfigurationId: number, stripeCouponCode: string, couponId : number) {
        this.stripeCouponId = stripeCouponId
        this.stripeConfigurationId = stripeConfigurationId
        this.stripeCouponCode = stripeCouponCode
        this.couponId = couponId
    }
}

export class XaccelStreamList {
    public streamId : number
    public streamName : string
    public streamProfileName : string
    public status : number

    constructor(streamId: number, streamName: string, streamProfileName: string, status : number) {
        this.streamId = streamId
        this.streamName = streamName
        this.streamProfileName = streamProfileName
        this.status = status
    }
}

export class XaccelVideoEncoder {
    public id: number
    public name: string
    public codec: string
    public bitrate: number
    public width: number
    public height: number
    public fps: number
    public gop: number

    constructor(
        id: number,
        name: string,
        codec: string,
        bitrate: number,
        width: number,
        height: number,
        fps: number,
        gop: number
    ) {
        this.id = id
        this.name = name
        this.codec = codec
        this.bitrate = bitrate
        this.width = width
        this.height = height
        this.fps = fps
        this.gop = gop
    }
}

export class XaccelAudioEncoder {
    public id: number
    public name: string
    public codec: string
    public bitrate: number
    public channels: number
    public sample_rate: number

    constructor(
        id: number,
        name: string,
        codec: string,
        bitrate: number,
        channels: number,
        sample_rate: number
    ) {
        this.id = id
        this.name = name
        this.codec = codec
        this.bitrate = bitrate
        this.channels = channels
        this.sample_rate = sample_rate
    }
}

export class XaccelStreamOutput {
    public id: number
    public protocol: string
    public url: string
    public select: string | boolean
    public server_id: number
    public stream_id: number
    public variants: any // depends on what xaccel returns
    public connections: number | null

    constructor(
        id: number,
        protocol: string,
        url: string,
        select: string | boolean,
        server_id: number,
        stream_id: number,
        variants: any,
        connections: number | null
    ) {
        this.id = id
        this.protocol = protocol
        this.url = url
        this.select = select
        this.server_id = server_id
        this.stream_id = stream_id
        this.variants = variants
        this.connections = connections
    }
}

// Full stream config (matches response-structure)
export class XaccelStreamConfig {
    public id: number
    public name: string
    public status: string
    public input_urls: string[]
    public headers: Record<string, string>
    public server_id: number
    public video_encoders: XaccelVideoEncoder[]
    public audio_encoders: XaccelAudioEncoder[]
    public outputs: XaccelStreamOutput[]

    constructor(
        id: number,
        name: string,
        status: string,
        input_urls: string[],
        headers: Record<string, string>,
        server_id: number,
        video_encoders: XaccelVideoEncoder[],
        audio_encoders: XaccelAudioEncoder[],
        outputs: XaccelStreamOutput[]
    ) {
        this.id = id
        this.name = name
        this.status = status
        this.input_urls = input_urls
        this.headers = headers
        this.server_id = server_id
        this.video_encoders = video_encoders
        this.audio_encoders = audio_encoders
        this.outputs = outputs
    }
}

export interface XaccelStreamMetadata {
    id: number
    name: string
    provider: string
    status: string
    inputs: string[]
    output: string | null
}

// b-api GET /calendar/state (CalendarController): what B-API holds in memory for the calendar.
// Times are ISO-8601 UTC strings. Only the master's B-API has data (state RUNNING).
export interface BapiAlarm {
    openedAt: string
    text: string            // the same text as the Slack message
}

export interface BapiStreamRow {
    streamId: string
    streamName: string
    streamProfileName: string
    status: string
    bitrate: number
    quality: number
    fps: number
    time: number            // running time in seconds
    speed: number
    errors: number
    nbConnections: number
}

export interface BapiEncoderRead {
    ok: boolean
    readAt: string
    lastOkAt: string | null
    failures: number
    streams: Record<string, BapiStreamRow>   // not-stopped streams only, key = stream id
}

export interface BapiFeed {
    cal: {
        calChannelId: number
        calMatchId: number
        match: string
        encoder: string
        encStreamId: string
        channelName: string
        outputUrl: string | null
        calStatus: string
        active: boolean
    }
    inCalendar: boolean
    startSendTime: string | null
    endSendTime: string | null
    startFailures: number
    stopFailures: number
    runningSince: string | null
    inputUrls: (string | null)[] | null     // null = not read by B-API yet
    outputUrls: (string | null)[] | null
    urlsReadAt: string | null
    alarms: Record<string, BapiAlarm>        // open alarms, key = alarm type
}

export interface CalendarState {
    state: 'DISABLED' | 'WAITING_FOR_MASTER' | 'SLAVE' | 'RUNNING'
    dryRun: boolean
    lastTickAt: string | null
    lastTickOkAt: string | null
    lastTickError: string | null
    systemAlarms: Record<string, BapiAlarm>  // key "BE" or the encoder name
    encoders: Record<string, BapiEncoderRead>
    feeds: BapiFeed[]
}

export interface StreamStats {
    streamId: number | string
    streamName: string
    streamProfileName: string
    status: string
    bitrate: number | string | null
    fps: number | string | null
    uptime: number | string | null
    speed: number | string | null
    errors: number | string | null
}

export class Event {
    public eventId : number
    public firstMatcher : string
    public secondMatcher : string
    public league : string
    public leagueId: string | undefined
    public orderNumber : number
    public application: string | undefined
    public status : boolean
    public active : boolean

    constructor(eventId: number, firstMatcher: string, secondMatcher: string, league: string, leagueId: string,
                orderNumber: number, application: string, status: boolean, active: boolean) {
        this.eventId = eventId
        this.firstMatcher = firstMatcher
        this.secondMatcher = secondMatcher
        this.league = league
        this.leagueId = leagueId
        this.orderNumber = orderNumber
        this.application = application
        this.status = status
        this.active = active
    }
}

export interface CalendarStaticData {
    sports: string[]
    calStatuses: string[]
    matchFormats: string[]
    languages: string[]
    defaultLeadMinutes: number
    defaultTrailMinutes: number
    maxPadSetupMinutes: number
}

export class CalLeague {
    public calLeagueId: number | undefined
    public name: string
    public shortName: string
    public sport: string
    public uid: string | undefined
    public calendarSourceUrl: string | undefined
    public lastImport: string | undefined
    public status: boolean
    public active: boolean
    public created: string | undefined
    public updated: string | undefined

    constructor(calLeagueId: number | undefined, name: string, shortName: string, sport: string, uid: string | undefined,
                calendarSourceUrl: string | undefined, lastImport: string | undefined, status: boolean, active: boolean, created?: string, updated?: string) {
        this.calLeagueId = calLeagueId
        this.name = name
        this.shortName = shortName
        this.sport = sport
        this.uid = uid
        this.calendarSourceUrl = calendarSourceUrl
        this.lastImport = lastImport
        this.status = status
        this.active = active
        this.created = created
        this.updated = updated
    }
}

export class CalMatch {
    public calMatchId: number
    public calLeagueId: number
    public uid: string
    public parentUid: string | undefined
    public team1: string | undefined
    public team2: string | undefined
    public venue: string | undefined
    public startTime: string
    public endTime: string | undefined
    public format: string | undefined
    public calStatus: string
    public status: boolean
    public active: boolean
    public configured?: boolean

    constructor(calMatchId: number, calLeagueId: number, uid: string, parentUid: string | undefined,
                team1: string | undefined, team2: string | undefined, venue: string | undefined, startTime: string,
                endTime: string | undefined, format: string | undefined, calStatus: string,
                status: boolean, active: boolean, configured?: boolean) {
        this.calMatchId = calMatchId
        this.calLeagueId = calLeagueId
        this.uid = uid
        this.parentUid = parentUid
        this.team1 = team1
        this.team2 = team2
        this.venue = venue
        this.startTime = startTime
        this.endTime = endTime
        this.format = format
        this.calStatus = calStatus
        this.status = status
        this.active = active
        this.configured = configured
    }
}

export class CalChannel {
    public calChannelId: number | undefined
    public calMatchId: number
    public encoder: string | undefined
    public encStreamId: string | undefined
    public encStreamName: string | undefined
    public channelName: string | undefined
    public origin: string | undefined
    public orderId: number | undefined
    public language: string | undefined
    // minutes before the match start / after the match end (its start when it has none) - the feed's window
    public leadMinutes: number | undefined
    public trailMinutes: number | undefined
    // response only: the window holds now - read-only (except its order) until it ends
    public onAir?: boolean
    public status: boolean
    public active: boolean

    constructor(calChannelId: number | undefined, calMatchId: number, encoder: string | undefined,
                encStreamId: string | undefined, encStreamName: string | undefined, channelName: string | undefined,
                origin: string | undefined, orderId: number | undefined, language: string | undefined, leadMinutes: number | undefined, trailMinutes: number | undefined,
                status: boolean, active: boolean) {
        this.calChannelId = calChannelId
        this.calMatchId = calMatchId
        this.encoder = encoder
        this.encStreamId = encStreamId
        this.encStreamName = encStreamName
        this.channelName = channelName
        this.origin = origin
        this.orderId = orderId
        this.language = language
        this.leadMinutes = leadMinutes
        this.trailMinutes = trailMinutes
        this.status = status
        this.active = active
    }
}

export class MatchEvent {
    public matchEventId: number | undefined
    public calMatchId: number
    public eventId: number | undefined
    public eventPadSetup: number | undefined
    public status: boolean
    public active: boolean
    // response only: the event window is on and the assignment was set up before it - read-only until the match ends
    public locked?: boolean

    constructor(matchEventId: number | undefined, calMatchId: number, eventId: number | undefined,
                eventPadSetup: number | undefined, status: boolean, active: boolean) {
        this.matchEventId = matchEventId
        this.calMatchId = calMatchId
        this.eventId = eventId
        this.eventPadSetup = eventPadSetup
        this.status = status
        this.active = active
    }
}

export class CalEventChannel {
    public calEventChannelId: number | undefined
    public matchEventId: number
    public calChannelId: number
    public active: boolean

    constructor(calEventChannelId: number | undefined, matchEventId: number, calChannelId: number, active: boolean) {
        this.calEventChannelId = calEventChannelId
        this.matchEventId = matchEventId
        this.calChannelId = calChannelId
        this.active = active
    }
}

export class ConflictMatch {
    public calMatchId: number
    public calLeagueId: number
    // The partner match's own start/end - used to open the day it is on when jumping to a conflict.
    public startTime: string | undefined
    public endTime: string | undefined

    constructor(calMatchId: number, calLeagueId: number, startTime?: string, endTime?: string) {
        this.calMatchId = calMatchId
        this.calLeagueId = calLeagueId
        this.startTime = startTime
        this.endTime = endTime
    }
}

export class ChannelConflict {
    public calChannelId: number
    public channelNameConflict: boolean
    public streamConflict: boolean
    public conflictingMatches: ConflictMatch[]

    constructor(calChannelId: number, channelNameConflict: boolean, streamConflict: boolean, conflictingMatches: ConflictMatch[] = []) {
        this.calChannelId = calChannelId
        this.channelNameConflict = channelNameConflict
        this.streamConflict = streamConflict
        this.conflictingMatches = conflictingMatches
    }
}

export class OccupiedEvent {
    public eventId: number
    public busyStart: string
    public busyEnd: string

    constructor(eventId: number, busyStart: string, busyEnd: string) {
        this.eventId = eventId
        this.busyStart = busyStart
        this.busyEnd = busyEnd
    }
}

export class CalMatchDraft {
    public uid: string
    public parentUid: string | undefined
    public team1: string | undefined
    public team2: string | undefined
    public venue: string | undefined
    public startTime: string
    public endTime: string | undefined
    public format: string | undefined
    public calStatus: string

    constructor(uid: string, parentUid: string | undefined, team1: string | undefined, team2: string | undefined,
                venue: string | undefined, startTime: string, endTime: string | undefined,
                format: string | undefined, calStatus: string) {
        this.uid = uid
        this.parentUid = parentUid
        this.team1 = team1
        this.team2 = team2
        this.venue = venue
        this.startTime = startTime
        this.endTime = endTime
        this.format = format
        this.calStatus = calStatus
    }
}

export class IcsPreviewResult {
    public matchesParsed: number
    public teamsMatched: number
    public errors: string[]
    public matches: CalMatchDraft[]
    public calendarUid: string | undefined
    // The calendar's own name from the .ics header (X-WR-CALNAME), if the file has one
    public calendarName: string | undefined
    // Series fixtures page found in the events' DESCRIPTION links - offered as the league's source URL
    public scheduleUrl: string | undefined

    constructor(matchesParsed: number, teamsMatched: number, errors: string[], matches: CalMatchDraft[], calendarUid?: string,
                calendarName?: string, scheduleUrl?: string) {
        this.matchesParsed = matchesParsed
        this.teamsMatched = teamsMatched
        this.errors = errors || []
        this.matches = matches || []
        this.calendarUid = calendarUid
        this.calendarName = calendarName
        this.scheduleUrl = scheduleUrl
    }
}

export class League {
    public leagueId : number
    public caption : string
    public description : string | undefined
    public logoFilename : string | undefined
    public logoAltText : string | undefined
    public application : string
    public status : boolean
    public active : boolean

    constructor(leagueId: number, caption: string, description: string, logoFilename: string, logoAltText: string,
                application: string, status: boolean, active: boolean) {
        this.leagueId = leagueId
        this.caption = caption
        this.description = description
        this.logoFilename = logoFilename
        this.logoAltText = logoAltText
        this.application = application
        this.status = status
        this.active = active
    }
}

export class Channel {
    public channelId : number
    public channelName : string
    public eventIds : number[]
    public caption : string
    public protectKey : string
    public tags? : string
    // channels.stream_app (the admin API sends it as appName): the app path of the channel's output URL
    public appName: string
    public edgePort: string
    public liveViews : number
    public totalViews : number
    public providersMetadata : string | undefined
    public orderNumber : number
    public status : boolean
    public active : boolean

    // Event link of the header application: eventId (null = unlink) + application. Both omitted = links untouched.
    public application?: string
    public eventId?: number | null

    constructor(channelId: number, channelName: string, eventIds: number[], caption: string, protectKey: string,
                tags: string,  appName: string, edgePort: string, liveViews: number,  totalViews: number, providersMetadata: string, orderNumber: number, status: boolean, active: boolean) {
        this.channelId = channelId
        this.channelName = channelName
        this.eventIds = eventIds
        this.caption = caption
        this.protectKey = protectKey
        this.tags = tags
        this.appName = appName
        this.edgePort = edgePort
        this.liveViews = liveViews
        this.totalViews = totalViews
        this.providersMetadata = providersMetadata
        this.orderNumber = orderNumber
        this.status = status
        this.active = active
    }
}

export class User {
    public userId : string
    public email : string
    public loginId : string
    public emailSubscribe : boolean
    public role : string
    public region : string
    public videoQuality : string
    public status : boolean
    public active : boolean
    public emailCleanStatus: boolean

    constructor(userId: string, email: string, loginId: string, emailSubscribe: boolean, role: string,
                region: string, videoQuality: string, status: boolean, active: boolean, emailCleanStatus: boolean) {
        this.userId = userId
        this.email = email
        this.loginId = loginId
        this.emailSubscribe = emailSubscribe
        this.role = role
        this.region = region
        this.videoQuality = videoQuality
        this.status = status
        this.active = active
        this.emailCleanStatus = emailCleanStatus
    }
}

export class UserRequest {
    public page : number
    public size : number
    public sort : Sort | undefined
    public filter : UserFilter | undefined

    constructor(page: number, size: number, sort?: Sort, filter?: UserFilter) {
        this.page = page
        this.size = size
        if (sort) this.sort = sort
        if (filter) this.filter = filter
    }
}

export class UserFilter {
    public userId: string
    public email: string
    public emailSubscribe: string
    public role: string
    public region: string
    public videoQuality: string
    public status: string
    public active: string
    public emailCleanStatus: string
    constructor(userId: string, email: string, role: string, emailSubscribe: string, region: string,
                videoQuality: string, status: string, active: string, emailCleanStatus: string) {
        this.userId = userId
        this.email = email
        this.role = role
        this.emailSubscribe = emailSubscribe
        this.region = region
        this.videoQuality = videoQuality
        this.status = status
        this.active = active
        this.emailCleanStatus = emailCleanStatus
    }
}

export class StripeUser {
    public stripeUserId : number
    public stripeConfigurationId : number
    public userId : string
    public stripeCustomerId : string

    constructor(stripeUserId: number, stripeConfigurationId: number, userId: string, stripeCustomerId: string) {
        this.stripeUserId = stripeUserId
        this.stripeConfigurationId = stripeConfigurationId
        this.userId = userId
        this.stripeCustomerId = stripeCustomerId
    }
}
export class StripeUserFilter {
    public stripeUserId : number
    public stripeConfigurationId : number
    public userId : string
    public email : string
    public stripeCustomerId : string

    constructor(stripeUserId: number, stripeConfigurationId: number, userId: string, email: string, stripeCustomerId: string) {
        this.stripeUserId = stripeUserId
        this.stripeConfigurationId = stripeConfigurationId
        this.userId = userId
        this.email = email
        this.stripeCustomerId = stripeCustomerId
    }
}

export class StripeUserRequest {
    public page : number
    public size : number
    public sort : Sort | undefined
    public filter : StripeUserFilter | undefined

    constructor(page: number, size: number, sort?: Sort, filter?: StripeUserFilter) {
        this.page = page
        this.size = size
        if (sort) this.sort = sort
        if (filter) this.filter = filter
    }
}

export class Sort {
    public column: string;
    public direction: SortDirection;

    constructor(column: string, direction: SortDirection) {
        this.column = column;
        this.direction = direction;
    }
}

enum SortDirection {
    ASC = 'ASC',
    DESC = 'DESC'
}

export class UserPlan  {

    public userPlanId : number
    public userId : string
    public planId : string
    public couponCode: string
    public startDate : string
    public endDate : string
    public trialActivationCode  : string
    public stripeInvoice : string
    public stripeConfigurationId : number
    public paypalConfigurationId : number
    public paypalOrderId : string
    public paypalCaptureId : string
    public plisioConfigurationId : number
    public plisioTxnId : string
    public squareConfigurationId : number
    public squareOrderId : string
    public cpayPayment : boolean
    public stripePaidAmount : number
    public stripePaymentDate : string
    public active : boolean
    public status : boolean
    public created : string
    public updated : string

    constructor(userPlanId: number, userId: string, planId: string, couponCode: string, startDate: string, endDate: string, trialActivationCode: string,
                stripeInvoice: string, stripeConfigurationId: number, paypalConfigurationId: number, paypalOrderId: string, paypalCaptureId: string,
                plisioConfigurationId: number, plisioTxnId: string, squareConfigurationId: number, squareOrderId: string, cpayPayment: boolean,
                stripePaidAmount: number, stripePaymentDate: string,
                active: boolean, status: boolean, created: string, updated: string) {
        this.userPlanId = userPlanId
        this.userId = userId
        this.planId = planId
        this.couponCode = couponCode
        this.startDate = startDate
        this.endDate = endDate
        this.trialActivationCode = trialActivationCode
        this.stripeInvoice = stripeInvoice
        this.stripeConfigurationId = stripeConfigurationId
        this.paypalConfigurationId = paypalConfigurationId
        this.paypalOrderId = paypalOrderId
        this.paypalCaptureId = paypalCaptureId
        this.plisioConfigurationId = plisioConfigurationId
        this.plisioTxnId = plisioTxnId
        this.squareConfigurationId = squareConfigurationId
        this.squareOrderId = squareOrderId
        this.cpayPayment = cpayPayment
        this.stripePaidAmount = stripePaidAmount
        this.stripePaymentDate = stripePaymentDate
        this.active = active
        this.status = status
        this.created = created
        this.updated = updated
    }
}

export class UserPlanFilter  {

    public userPlanId : number
    public userId : string
    public email : string
    public planId : string
    public couponCode: string
    public startDate : string
    public endDate : string
    public trialActivationCode  : string
    public stripeInvoice : string
    public stripeConfigurationId : number
    public paypalConfigurationId : number
    public paypalOrderId : string
    public paypalCaptureId : string
    public plisioConfigurationId : number
    public plisioTxnId : string
    public squareConfigurationId : number
    public squareOrderId : string
    public cpayPayment : boolean
    public stripePaidAmount : number
    public stripePaymentDate : string
    public active : boolean
    public status : boolean
    public created : string
    public updated : string

    constructor(userPlanId: number, userId: string, email: string, planId: string, couponCode: string, startDate: string, endDate: string, trialActivationCode: string,
                stripeInvoice: string, stripeConfigurationId: number, paypalConfigurationId: number, paypalOrderId: string, paypalCaptureId: string,
                plisioConfigurationId: number, plisioTxnId: string, squareConfigurationId: number, squareOrderId: string,
                cpayPayment: boolean, stripePaidAmount: number, stripePaymentDate: string,
                active: boolean, status: boolean, created: string, updated: string) {
        this.userPlanId = userPlanId
        this.userId = userId
        this.email = email
        this.planId = planId
        this.couponCode = couponCode
        this.startDate = startDate
        this.endDate = endDate
        this.trialActivationCode = trialActivationCode
        this.stripeInvoice = stripeInvoice
        this.stripeConfigurationId = stripeConfigurationId
        this.paypalConfigurationId = paypalConfigurationId
        this.paypalOrderId = paypalOrderId
        this.paypalCaptureId = paypalCaptureId
        this.plisioConfigurationId = plisioConfigurationId
        this.plisioTxnId = plisioTxnId
        this.squareConfigurationId = squareConfigurationId
        this.squareOrderId = squareOrderId
        this.cpayPayment = cpayPayment
        this.stripePaidAmount = stripePaidAmount
        this.stripePaymentDate = stripePaymentDate
        this.active = active
        this.status = status
        this.created = created
        this.updated = updated
    }
}

export enum PaymentMethod {
    Stripe = "Stripe",
    PayPal = "PayPal",
    Plisio = "Plisio",
    Square = "Square",
    CPay = "CPay",
}
export class UserPlanRequest {
    public page : number
    public size : number
    public sort : Sort | undefined
    public filter : UserPlanFilter | undefined
    public application : string | undefined

    constructor(page: number, size: number, sort?: Sort, filter?: UserPlanFilter, application?: string) {
        this.page = page
        this.size = size
        if (sort) this.sort = sort
        if (filter) this.filter = filter
        if (application) this.application = application
    }
}

export class Session {
    public sessionId : number
    public userId : string
    public edgeAddress : string
    public protocol : string
    public channelId : number
    public vidictId: number;
    public region : string | undefined
    public deviceOs: DeviceOs | undefined
    public deviceType?: DeviceType | undefined
    public userAgent?: string | undefined
    public appVersion?: string | undefined
    public randomUUID : string | undefined
    public application : string | undefined
    public startTime : string | undefined
    public endTime : string | undefined

    constructor(sessionId: number, userId: string, edgeAddress: string, protocol: string, channelId: number, vidictId: number, region?: string,
                deviceOs?: DeviceOs, deviceType?: DeviceType, userAgent?: string,  appVersion?: string, randomUUID?: string, application?: string, startTime?: string, endTime?: string) {
        this.sessionId = sessionId
        this.userId = userId
        this.edgeAddress = edgeAddress
        this.protocol = protocol
        this.channelId = channelId
        this.vidictId = vidictId
        if (region) this.region = region
        if (deviceOs) this.deviceOs = deviceOs
        if (deviceType) this.deviceType = deviceType
        if (userAgent) this.userAgent = userAgent
        if (appVersion) this.appVersion = appVersion
        if (randomUUID) this.randomUUID = randomUUID
        if (application) this.application = application
        if (startTime) this.startTime = startTime
        if (endTime) this.endTime = endTime
    }
}
export class SessionFilter {
    public sessionId : number
    public userId : string
    public email : string
    public edgeAddress : string
    public protocol : string
    public channelId : number
    public vidictId: number;
    public region : string | undefined
    public deviceOs: DeviceOs | undefined
    public deviceType?: DeviceType | undefined
    public appVersion?: string | undefined
    public startTime : number | undefined
    public endTime : number | undefined
    public isEndTimeNull : boolean | undefined

    constructor(sessionId: number, userId: string, email: string, edgeAddress: string, protocol: string, channelId: number, vidictId: number, region?: string,
                deviceOs?: DeviceOs, deviceType?: DeviceType, appVersion?: string, startTime?: number, endTime?: number, isEndTimeNull?: boolean) {
        this.sessionId = sessionId
        this.userId = userId
        this.email = email
        this.edgeAddress = edgeAddress
        this.protocol = protocol
        this.channelId = channelId
        this.vidictId = vidictId
        if (region) this.region = region
        if (deviceOs) this.deviceOs = deviceOs
        if (deviceType) this.deviceType = deviceType
        if (appVersion) this.appVersion = appVersion
        if (startTime) this.startTime = startTime
        if (endTime) this.endTime = endTime
        if (isEndTimeNull) this.isEndTimeNull = isEndTimeNull
    }
}
export class EmailRequest {
    public typeUser : string
    public monthsAgo : number | undefined
    public startDate : number | undefined
    public endDate : number | undefined
    constructor(typeUser: string, monthsAgo?: number, startDate?: number, endDate?: number) {
        this.typeUser = typeUser
        this.monthsAgo = monthsAgo
        if (startDate) this.startDate = startDate;
        if (endDate) this.endDate = endDate;
    }
}

export class SessionRequest {
    public page : number
    public size : number
    public sort : Sort | undefined
    public filter : SessionFilter | undefined

    constructor(page: number, size: number, sort?: Sort, filter?: SessionFilter) {
        this.page = page
        this.size = size
        if (sort) this.sort = sort
        if (filter) this.filter = filter
    }
}

// One row's setup state on All Matches - the same warnings Daily shows on a match card.
export interface CalMatchSetupSummary {
    calMatchId: number
    channelCount: number
    eventCount: number
    applications: string[]
    channelConflict: boolean
    eventDoubleBooked: boolean
}

// POST /cal-league/match/list-paged - All Matches / All Match Events. Same page/size/sort/filter
// shape as SessionRequest; page is 0-based. from/to are UTC ISO instants, both or neither.
// upcomingOnly = only matches that haven't ended yet; conflictsOnly = only active matches with a
// channel conflict or a double-booked event. Left undefined, they aren't filtered on.
export class CalMatchPageFilter {
    public from : string | undefined
    public to : string | undefined
    public upcomingOnly : boolean | undefined
    public conflictsOnly : boolean | undefined

    constructor(from?: string, to?: string, upcomingOnly?: boolean, conflictsOnly?: boolean) {
        if (from) this.from = from
        if (to) this.to = to
        if (upcomingOnly !== undefined) this.upcomingOnly = upcomingOnly
        if (conflictsOnly !== undefined) this.conflictsOnly = conflictsOnly
    }
}

export class CalMatchPageRequest {
    public page : number
    public size : number
    public sort : Sort | undefined
    public filter : CalMatchPageFilter | undefined

    constructor(page: number, size: number, sort?: Sort, filter?: CalMatchPageFilter) {
        this.page = page
        this.size = size
        if (sort) this.sort = sort
        if (filter) this.filter = filter
    }
}

// conflictMatchCount / nextConflictAt: active, not-yet-ended matches (inside the filter window, if
// any) owning a conflicting channel, and the earliest start among them (null when there are none).
export interface CalMatchPageResponse {
    calMatchList: CalMatch[]
    page: number
    size: number
    totalElements: number
    filter: CalMatchPageFilter | null
    sort: Sort | null
    setupSummaries: CalMatchSetupSummary[]
    conflictMatchCount: number
    nextConflictAt: string | null
}

export class RefundRequest {
    public userPlanId : number
    public amount : number
    public reason : string | undefined
    constructor(userPlanId: number, amount: number, reason?: string) {
        this.userPlanId = userPlanId
        this.amount = amount
        if (reason) this.reason = reason
    }
}

export class CDNRegion {
    public cdnRegionKey : string
    public cdnRegionValue : string
    public active : boolean
    public application : string
    public updated : string

    constructor(cdnRegionKey: string, cdnRegionValue: string, active: boolean, application: string, updated: string) {
        this.cdnRegionKey = cdnRegionKey
        this.cdnRegionValue = cdnRegionValue
        this.active = active
        this.application = application
        this.updated = updated
    }
}

export class Region {
    public countryIsoCode : string
    public region : string

    constructor(countryIsoCode: string, region: string) {
        this.countryIsoCode = countryIsoCode
        this.region = region
    }
}

export class VideoQuality {
    public videoQualityKey : string
    public videoQualityValue : string | undefined
    public active : boolean
    public application : string
    public updated : string

    constructor(videoQualityKey: string, videoQualityValue: string, active: boolean, application: string, updated: string) {
        this.videoQualityKey = videoQualityKey
        this.videoQualityValue = videoQualityValue
        this.active = active
        this.application = application
        this.updated = updated
    }
}

export class CDNRegionDeactivate {
    public deactivateCdnRegionKey : string
    public replaceCdnRegionKey : string

    constructor(deactivateCdnRegionKey: string, replaceCdnRegionKey: string) {
        this.deactivateCdnRegionKey = deactivateCdnRegionKey
        this.replaceCdnRegionKey = replaceCdnRegionKey
    }
}

export class Application {

    public application : string
    public domains : string | undefined
    public description : string | undefined
    public hlsPrefix : string | undefined
    public hlsSufix : string | undefined
    public edgePort : string
    public appName : string
    // Same Sport enum values the calendar screens use (CalendarStaticData.sports) - not a
    // separate application-specific list.
    public sport : string | undefined
    public vidictLicense : string
    public vidictSecret : string
    public emailTemplates : string
    public active : boolean

    constructor(application: string, domains: string, description: string, hlsPrefix: string, hlsSufix: string, edgePort: string,
                appName: string, sport: string | undefined, vidictLicense: string, vidictSecret: string, emailTemplates: string, active: boolean) {
        this.application = application
        this.domains = domains
        this.description = description
        this.hlsPrefix = hlsPrefix
        this.hlsSufix = hlsSufix
        this.edgePort = edgePort
        this.appName = appName
        this.sport = sport
        this.vidictLicense = vidictLicense
        this.vidictSecret = vidictSecret
        this.emailTemplates = emailTemplates
        this.active = active
    }
}

export class System {
    public name : string
    public value : string | undefined
    public application : string

    constructor(name: string, value: string, application: string) {
        this.name = name
        this.value = value
        this.application = application
    }
}

export class StatusResponse {
    public code: number
    public userId: string
    public message: string
    public timeStamp: number

    constructor(code: number, userId: string, message: string, timeStamp: number) {
        this.code = code
        this.userId = userId
        this.message = message
        this.timeStamp = timeStamp
    }
}

export interface AdminUserDevice {
    deviceId: string
    deviceOs: DeviceOs | undefined
    deviceType: DeviceType | undefined
    deviceName: string | null
    website: string
    firstLoginAt: string
    lastSeenAt: string
    lastIp: string | null
    active: boolean
}

export enum DeviceOs {
    ANDROID = "ANDROID",
    IOS = "IOS",
    WEB = "WEB",
    ANDROID_TV = "ANDROID_TV",
    LINUX = "LINUX",
    MACOS = "MACOS",
    WINDOWS = "WINDOWS",
    OTHER_OS = "OTHER_OS"
}

export enum DeviceType {
    CHROME = "CHROME",
    SAFARI = "SAFARI",
    EDGE = "EDGE",
    FIREFOX = "FIREFOX",
    SAMSUNG_INTERNET = "SAMSUNG_INTERNET",
    OPERA = "OPERA",
    APP = "APP",
    OTHER_BROWSER = "OTHER_BROWSER"
}

//Should there be userRequest and userResponse

// One match the default sources were NOT written to: it clashes with another match of the league in the same
// window (OVERLAPS_LEAGUE_MATCH) or with an existing source anywhere (CONFLICTS_EXISTING).
export interface DefaultSourceNotSet {
    calMatchId: number
    team1: string | undefined
    team2: string | undefined
    startTime: string
    reason: string
    details: string
}

export interface ApplyDefaultSourcesResult {
    matchesApplied: number
    sourcesWritten: number
    notSet: DefaultSourceNotSet[]
}
